import type { Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"
import { Box, ButtonGroup, TabScrollButton, Tooltip } from "@mui/material"

import useScrollChevrons from "helpers/hooks/useScrollChevrons"
import { TabButton } from "widgets/styled/buttons"

const CHEVRON_WIDTH = 28
const chevronStyles = {
  position: "absolute",
  top: 0,
  bottom: 0,
  width: CHEVRON_WIDTH,
  opacity: 1,
  bgcolor: "background.paper",
} as const

interface TabButtonGroupProps {
  buttons: { name: string; value: number; tooltip?: string }[]
  tab: number
  setTab: Dispatch<SetStateAction<number>>
}

const TabButtonGroup = ({ buttons, tab, setTab }: TabButtonGroupProps) => {
  const { t } = useTranslation()
  // When the buttons don't fit they scroll, with chevrons like the MUI tabs over the edges.
  const { scrollRef, selectedRef, canScroll, scroll } = useScrollChevrons(
    tab,
    CHEVRON_WIDTH,
  )

  return (
    <Box position="relative">
      {canScroll.left && (
        <TabScrollButton
          direction="left"
          orientation="horizontal"
          onClick={() => scroll(-1)}
          sx={{ ...chevronStyles, left: 0, zIndex: 1 }}
        />
      )}
      <Box
        ref={scrollRef}
        sx={{
          position: "relative",
          minWidth: 0,
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <ButtonGroup variant="outlined" size="small">
          {buttons.map((button) => {
            const btn = (
              <TabButton
                key={button.value}
                ref={tab === button.value ? selectedRef : undefined}
                variant={tab === button.value ? "contained" : "outlined"}
                onClick={() => setTab(button.value)}
                value={button.value}
              >
                {t(button.name)}
              </TabButton>
            )
            return button.tooltip ? (
              <Tooltip key={button.value} title={t(button.tooltip)} arrow>
                <span>{btn}</span>
              </Tooltip>
            ) : (
              btn
            )
          })}
        </ButtonGroup>
      </Box>
      {canScroll.right && (
        <TabScrollButton
          direction="right"
          orientation="horizontal"
          onClick={() => scroll(1)}
          sx={{ ...chevronStyles, right: 0, zIndex: 1 }}
        />
      )}
    </Box>
  )
}

export default TabButtonGroup
